<template>
  <component
    :is="resolvedIcon"
    v-if="resolvedIcon"
    :size="size"
    :color="color || 'currentColor'"
    :stroke-width="strokeWidth"
  />
  <img
    v-else-if="resolvedImage"
    :src="resolvedImage"
    :width="imageWidth || size"
    :height="imageHeight || size"
    :alt="name"
    :style="{
      filter: colorToFilter(color),
    }"
  />
</template>

<script setup>
import { computed } from "vue";
import {
  Trophy,
  Calendar,
  Newspaper,
  Users,
  MapPin,
  User,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  ArrowUpRight,
  Phone,
  Mail,
  Twitter,
  Link2,
  Copy,
  Check,
  FileText,
  File,
  Download,
  Building2,
  Landmark,
  Map,
  Home,
  Heart,
  CalendarDays,
  CalendarClock,
  Camera,
  CalendarSearch,
  Clock,
  MessageCircleHeart,
  UserStar,
  Hourglass,
  Hotel,
  Handshake,
  PhoneCall,
  Table,
  GraduationCap,
  LogOut,
  Eye,
  EyeOff,
  Edit3,
  ShieldCheck,
  Trash2,
  Upload,
  Settings,
} from "lucide-vue-next";

// SVGs/PNGs personalizados para redes sociales (marcas registradas que no
// existen en Lucide). Vue CLI los importa como URL de recurso.
import whatsappIcon from "@/assets/images/social/whatsapp.svg";
import facebookIcon from "@/assets/images/social/facebook.svg";
import instagramIcon from "@/assets/images/social/instagram.svg";
import federacionIcon from "@/assets/images/logos/federacionBochas1.png";

const iconMap = {
  trophy: Trophy,
  calendar: Calendar,
  newspaper: Newspaper,
  users: Users,
  location: MapPin,
  "chevron-left": ChevronLeft,
  "chevron-right": ChevronRight,
  "chevron-down": ChevronDown,
  "chevron-up": ChevronUp,
  "arrow-right": ArrowRight,
  "arrow-up-right": ArrowUpRight,
  phone: Phone,
  mail: Mail,
  doc: FileText,
  home: Home,
  download: Download,
  albergue: GraduationCap,
  casa: Home,
  corazón: Heart,
  calendarDays: CalendarDays,
  calendarSearch: CalendarSearch,
  camara: Camera,
  reloj: Clock,
  calendario: Calendar,
  mensaje: MessageCircleHeart,
  futuro: UserStar,
  pasado: Hourglass,
  hourglass: Hourglass,
  alojamiento: Hotel,
  acompaniamiento: Handshake,
  calendarClock: CalendarClock,
  user: User,
  building: Building2,
  landmark: Landmark,
  map: Map,
  phoneCall: PhoneCall,
  file: File,
  twitter: Twitter,
  link: Link2,
  copy: Copy,
  check: Check,
  categories: Table,
  "log-out": LogOut,
  eye: Eye,
  "eye-off": EyeOff,
  edit: Edit3,
  trash: Trash2,
  shield: ShieldCheck,
  upload: Upload,
  settings: Settings,
};

const imageMap = {
  whatsapp: whatsappIcon,
  facebook: facebookIcon,
  instagram: instagramIcon,
  federación: federacionIcon,
};

const props = defineProps({
  name: { type: String, required: true },
  size: { type: [Number, String], default: 20 },
  color: { type: String, default: "" },
  strokeWidth: { type: [Number, String], default: 2 },
  imageWidth: { type: [Number, String], default: null },
  imageHeight: { type: [Number, String], default: null },
});

const resolvedIcon = computed(() => iconMap[props.name] || null);
const resolvedImage = computed(() => imageMap[props.name] || null);

// Convierte un color hex simple a un filtro CSS para "teñir" un PNG/SVG.
// Útil para los íconos de redes (WhatsApp, Facebook, Instagram) que vienen
// en sus colores de marca y querés blanquearlos o teñirlos al color del
// contexto. Si pasás un color que no está en el map, se devuelve undefined
// y no se aplica ningún filtro (queda el logo en su color original).
const colorToFilter = (color) => {
  if (!color) return undefined;
  const map = {
    "#fff": "invert(1) brightness(100%)",
    white: "invert(1) brightness(100%)",
    // Rojo de marca (#a52121): filtro que convierte cualquier color a ese rojo
    "#a52121":
      "brightness(0) saturate(100%) invert(18%) sepia(95%) saturate(7464%) hue-rotate(355deg) brightness(95%) contrast(95%)",
  };
  return map[color.toLowerCase()] || undefined;
};
</script>
